/**
 * Template de prise de besoin : Procédure de gestion des incidents (ITIL) v1
 * Aligné sur ITIL 4, COBIT 2019 et ISO 27001
 * Procédure opérationnelle détaillée pour la gestion des incidents TI
 */

import type { IntakeTemplate } from '../templateTypes';

export const templateProcedureGestionIncidentsV1: IntakeTemplate = {
  id: 'procedure-gestion-incidents-v1',
  name: 'Prise de besoin Procédure de gestion des incidents (ITIL) v1',
  description:
    'Questionnaire complet pour élaborer une procédure de gestion des incidents conforme ITIL 4, COBIT 2019 et ISO 27001. Définit les étapes, workflows, rôles et responsabilités pour la détection, enregistrement, classification, résolution et fermeture des incidents.',
  documentTypeCode: 'INCIDENT_MANAGEMENT_PROCEDURE',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Enregistrement',
      description: 'Informations sur l\'entreprise et processus d\'enregistrement',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour la procédure.',
        },
        {
          id: 'outil_ticketing',
          label: 'Quel outil de ticketing est utilisé pour la gestion des incidents ?',
          type: 'text',
          placeholder: 'Ex: Jira, ServiceNow, Zendesk, système interne...',
          required: true,
          helpText: 'ITIL 4: Outil de gestion des incidents pour traçabilité.',
        },
        {
          id: 'canaux_reception',
          label: 'Quels canaux sont utilisés pour recevoir les incidents ?',
          type: 'multiselect',
          options: [
            'Billet (ERP)',
            'Courriel',
            'Appel téléphonique',
            'Portail self-service',
            'Chat',
            'Alerte automatique',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Canaux de réception pour accessibilité.',
        },
        {
          id: 'processus_enregistrement',
          label: 'Quel est le processus d\'enregistrement d\'un incident ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Réception → Création ticket → Attribution → Classification...',
          helpText: 'ITIL 4: Processus structuré pour enregistrement efficace.',
        },
        {
          id: 'ton_document_procedure',
          label: 'Quel ton souhaitez-vous pour cette procédure ?',
          type: 'select',
          options: [
            'Formel et procédural (ton impératif, étapes claires)',
            'Pédagogique et explicatif (ton guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la facilité d\'utilisation de la procédure.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Classification & Priorisation',
      description: 'Processus de classification et priorisation',
      icon: 'Target',
      questions: [
        {
          id: 'systeme_classification',
          label: 'Quel système de classification est utilisé ?',
          type: 'select',
          options: ['ITIL P1-P4', 'NIST CAT 1-5', 'Autre', 'À déterminer'],
          required: true,
          helpText: 'ITIL 4: Système de classification pour priorisation.',
        },
        {
          id: 'criteres_classification',
          label: 'Quels critères sont utilisés pour classifier un incident ?',
          type: 'multiselect',
          options: [
            'Impact business',
            'Urgence',
            'Nombre d\'utilisateurs affectés',
            'Criticité du service',
            'Impact financier',
            'Conformité réglementaire',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Critères pour classification cohérente.',
        },
        {
          id: 'qui_classifie',
          label: 'Qui est responsable de la classification initiale ?',
          type: 'text',
          placeholder: 'Ex: Technicien de première ligne, Gestionnaire d\'incidents...',
          required: true,
          helpText: 'ITIL 4: Responsable pour classification rapide.',
        },
        {
          id: 'processus_classification',
          label: 'Quel est le processus de classification ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Évaluation → Application critères → Attribution priorité...',
          helpText: 'ITIL 4: Processus structuré pour classification efficace.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Résolution & Workflows',
      description: 'Processus de résolution et workflows',
      icon: 'FileCheck',
      questions: [
        {
          id: 'niveaux_support',
          label: 'Combien de niveaux de support avez-vous ?',
          type: 'select',
          options: ['1 niveau', '2 niveaux', '3 niveaux', '4 niveaux', 'À déterminer'],
          required: true,
          helpText: 'ITIL 4: Structure de support pour résolution efficace.',
        },
        {
          id: 'responsabilites_niveaux',
          label: 'Quelles sont les responsabilités de chaque niveau ?',
          type: 'textarea',
          placeholder: 'Décrivez: Niveau 1 → résolution simple, Niveau 2 → résolution complexe, Niveau 3 → experts...',
          helpText: 'ITIL 4: Responsabilités claires pour chaque niveau.',
        },
        {
          id: 'workflow_resolution',
          label: 'Quel est le workflow de résolution d\'un incident ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Enregistrement → Classification → Attribution → Investigation → Résolution → Vérification → Fermeture...',
          helpText: 'ITIL 4: Workflow structuré pour résolution efficace.',
        },
        {
          id: 'delais_resolution',
          label: 'Quels sont les délais cibles de résolution par priorité ?',
          type: 'textarea',
          placeholder: 'Ex: P1: 1h, P2: 4h, P3: 1 jour, P4: 3 jours...',
          helpText: 'ITIL 4: Délais cibles pour mesurer la performance.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Fermeture & Amélioration',
      description: 'Processus de fermeture et amélioration continue',
      icon: 'CheckCircle',
      questions: [
        {
          id: 'criteres_fermeture',
          label: 'Quels sont les critères de fermeture d\'un incident ?',
          type: 'multiselect',
          options: [
            'Service restauré',
            'Utilisateur confirmé résolution',
            'Vérification post-résolution',
            'Documentation complète',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Critères pour fermeture appropriée.',
        },
        {
          id: 'processus_fermeture',
          label: 'Quel est le processus de fermeture ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Résolution → Vérification → Confirmation utilisateur → Fermeture → Documentation...',
          helpText: 'ITIL 4: Processus structuré pour fermeture efficace.',
        },
        {
          id: 'activites_post_incident',
          label: 'Quelles activités post-incident sont prévues ?',
          type: 'multiselect',
          options: [
            'Satisfaction client',
            'Analyse de cause racine',
            'Leçons apprises',
            'Amélioration des procédures',
            'Formation supplémentaire',
            'Autre',
          ],
          helpText: 'ITIL 4: Activités pour amélioration continue.',
        },
        {
          id: 'revision_procedure',
          label: 'À quelle fréquence souhaitez-vous réviser cette procédure ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Après chaque incident majeur', 'À déterminer'],
          helpText: 'ITIL 4: Révision régulière essentielle pour maintenir l\'efficacité.',
        },
      ],
    },
  ],
};





